import { useCallback } from 'react'
import { logEvent } from '../api/hooks/logs/api'

/**
 * Hook to log events with type-safety
 *
 * @param {string} eventType - Event type from EVENTS enum
 * @returns {Function} log function that accepts event data
 *
 * @example
 * const logWorkspaceSwitch = useEvent(EVENTS.WORKSPACE.SWITCH)
 * logWorkspaceSwitch({ workspaceId: '123', workspaceName: 'My Workspace' })
 */
export function useEvent(eventType) {
    return useCallback(
        (data = {}) => {
            logEvent(eventType, { type: eventType, ...data })
        },
        [eventType]
    )
}

/**
 * Hook to get multiple event loggers
 *
 * @example
 * const { logCanvasCreate, logCanvasDelete } = useEvents({
 *     logCanvasCreate: EVENTS.CANVAS.CREATE_BLANK,
 *     logCanvasDelete: EVENTS.CANVAS.DELETE,
 * })
 */
export function useEvents(eventMap) {
    const loggers = {}
    for (const [name, eventType] of Object.entries(eventMap)) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        loggers[name] = useEvent(eventType)
    }
    return loggers
}

/**
 * Imperative event logging (for non-React contexts like error boundaries, utils)
 *
 * @example
 * trackEvent(EVENTS.ERROR.OOPS, { message: error.message })
 */
export function trackEvent(eventType, data = {}) {
    return logEvent(eventType, { type: eventType, ...data })
}
